import React, { useState } from 'react';
import { Container, Row, Col, Button, Tab, Tabs } from 'react-bootstrap';
import { postRequest } from './Services/HttpClient';
import VarnasaaraVisualization from './VarnasaaraVisualization';
import SanskritKeyboard from './SanskritKeyboard';
import './Varnakrama.css';

const Varnakrama = (props) => {
    const [inputWord, setInputWord] = useState('');
    const [veda, setVeda] = useState('yajur');
    const [outputType, setOutputType] = useState('sarve');
    const [result, setResult] = useState(null);
    const [varnasaaraData, setVarnasaaraData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [varnasaaraLoading, setVarnasaaraLoading] = useState(false);
    const [varnasaaraError, setVarnasaaraError] = useState(null);
    const [showAll, setShowAll] = useState(false);
    const [activeTab, setActiveTab] = useState('varnasaara');
    const [showKeyboard, setShowKeyboard] = useState(false);

    // Helper function to call varnakrama API with V2 fallback to V1
    const callVarnakramaAPI = async (requestBody) => {
      try {
        console.log('Trying V2 API endpoint...');
        const response = await postRequest('v2/varnakrama', JSON.stringify(requestBody));
        console.log('V2 API successful');
        return response;
      } catch (error) {
        console.warn('V2 API failed, falling back to V1:', error);
        try {
          const response = await postRequest('varnakrama', JSON.stringify(requestBody));
          console.log('V1 API fallback successful');
          return response;
        } catch (v1Error) {
          console.error('Both V2 and V1 APIs failed:', v1Error);
          throw v1Error;
        }
      }
    };

    // New function to call varnasaara JSON API
    const callVarnasaaraAPI = async (word, vedaType) => {
      try {
        const requestBody = {
          word: word,
          veda: vedaType,
          output_format: 'json'
        };
        console.log('Calling varnasaara JSON API...');
        const response = await postRequest('v2/varnasaara/json', JSON.stringify(requestBody));
        console.log('Got JSON format from API!');
        return response.varnasaara;
      } catch (error) {
        console.error('Varnasaara JSON API failed:', error);
        throw error;
      }
    };

    const handleAnalyze = async ()=> {
      if (!inputWord.trim()) return;

      setLoading(true);
      setVarnasaaraLoading(true);
      setVarnasaaraError(null);

      try {
        const requestBody = {
          word: inputWord,
          veda: veda,
          compute_shuddha: outputType === 'shuddha' || outputType === 'sarve',
          compute_svara: outputType === 'svara' || outputType === 'sarve',
          compute_maatra: outputType === 'maatra' || outputType === 'sarve',
          compute_anga: outputType === 'anga' || outputType === 'sarve',
          compute_varnasaara: outputType === 'varnasaara' || outputType === 'sarve'
        };
        const response = await callVarnakramaAPI(requestBody);
        setResult(response);
        setShowAll(outputType === 'sarve');

        // Also load varnasaara data
        try {
          const varnasaaraData = await callVarnasaaraAPI(inputWord, veda);
          setVarnasaaraData(varnasaaraData);
        } catch (varnasaaraError) {
          console.error('Varnasaara loading failed:', varnasaaraError);
          setVarnasaaraError('वर्णसार विश्लेषणे त्रुटिः।');
        }
      } catch (error) {
        console.error('Error:', error);
        alert('Failed to analyze. Please try again.');
      } finally {
        setLoading(false);
        setVarnasaaraLoading(false);
      }
    }

    const handleVarnasaaraAnalyze = async () => {
      if (!inputWord.trim()) return;

      setVarnasaaraLoading(true);
      setVarnasaaraError(null);
      try {
        const data = await callVarnasaaraAPI(inputWord, veda);
        setVarnasaaraData(data);
      } catch (error) {
        console.error('Varnasaara analysis error:', error);
        setVarnasaaraError('वर्णसार विश्लेषणे त्रुटिः। कृपया पुनः प्रयासं कुर्वन्तु।');
      } finally {
        setVarnasaaraLoading(false);
      }
    };

    const handleTabSelect = (key) => {
      setActiveTab(key);
      if (key === 'varnasaara' && inputWord.trim() && !varnasaaraData && !varnasaaraLoading) {
        handleVarnasaaraAnalyze();
      }
    };

    const handleClear = () => {
      setInputWord('');
      setResult(null);
      setVarnasaaraData(null);
      setVarnasaaraError(null);
      setShowAll(false);
    };

    const handleKeyboardInput = (key) => {
      if (key === 'BACKSPACE') {
        setInputWord(prev => prev.slice(0, -1));
      } else {
        setInputWord(prev => prev + key);
      }
      setVarnasaaraData(null);
      setVarnasaaraError(null);
    };

    const renderAllResults = () => {
      if (!showAll || !result) return null;

      const sections = [
        { key: 'shuddha', title: 'शुद्ध-वर्णक्रमः', content: result.shuddha },
        { key: 'svara', title: 'स्वर-वर्णक्रमः', content: result.svara },
        { key: 'maatra', title: 'मात्रा-वर्णक्रमः', content: result.maatra },
        { key: 'anga', title: 'अङ्ग-वर्णक्रमः', content: result.anga },
        { key: 'varnasaara', title: 'वर्णसार-वर्णक्रमः', content: result.varnasaara }
      ];

      return (
        <div className="varnakrama-results">
          {sections.map((section) => (
            section.content && (
              <div key={section.key} className="varnakrama-result-section">
                <h3 className="varnakrama-result-title">{section.title}</h3>
                <div className="varnakrama-result-content">
                  <div style={{ fontWeight: 'bold', fontSize: '1.2em', marginBottom: '10px', color: '#2c3e50' }}>
                    {inputWord}
                  </div>
                  {(section.key === 'varnasaara' || section.key === 'maatra' || section.key === 'anga') ?
                    (() => {
                      if (section.key === 'maatra' || section.key === 'anga') {
                        const content = section.content;
                        const parts = content.split('विरामाः।');
                        return (
                          <div style={{ whiteSpace: 'pre-line' }}>
                            {parts.map((part, partIndex) => {
                              const trimmedPart = part.trim();
                              if (!trimmedPart) return null;
                              return (
                                <div key={partIndex} style={{ marginBottom: partIndex < parts.length - 1 ? '15px' : '0' }}>
                                  {trimmedPart}{partIndex < parts.length - 1 ? 'विरामाः।' : ''}
                                </div>
                              );
                            }).filter(Boolean)}
                          </div>
                        );
                      }

                      const trimmedContent = section.content.trim();
                      if (!trimmedContent) return null;

                      const lines = trimmedContent.split('\n').reduce((acc, line) => {
                        const trimmedLine = line.trim();
                        if (!trimmedLine) return acc;
                        if (trimmedLine === '।' && acc.length > 0) {
                          acc[acc.length - 1] += '।';
                          return acc;
                        }
                        acc.push(trimmedLine);
                        return acc;
                      }, []);

                      return (
                        <div style={{ whiteSpace: 'pre-line' }}>
                          {lines.map((line, lineIndex) => {
                            const trimmedLine = line.trim();
                            if (!trimmedLine) return null;
                            const firstSpaceIndex = trimmedLine.indexOf(' ');
                            const firstSyllable = firstSpaceIndex === -1 ? trimmedLine : trimmedLine.substring(0, firstSpaceIndex);
                            const restOfLine = firstSpaceIndex === -1 ? '' : trimmedLine.substring(firstSpaceIndex);
                            return (
                              <div key={lineIndex}>
                                <span style={{ fontWeight: 'bold', color: '#2c3e50', fontSize: '1.1em' }}>{firstSyllable}</span>
                                {restOfLine}
                              </div>
                            );
                          }).filter(Boolean)}
                        </div>
                      );
                    })() :
                    section.content
                  }
                </div>
              </div>
            )
          ))}
        </div>
      );
    }

   return (
    <Container fluid className="varnakrama-page">
      {/* Header */}
      <Row>
        <Col>
          <div className="varnakrama-header">
            <h1 className="varnakrama-title">वर्णक्रमः (Varnakrama)</h1>
            <p className="varnakrama-description">Syllable-wise analysis of Sanskrit text</p>
          </div>
        </Col>
      </Row>

      {/* Options Row */}
      <Row className="varnakrama-options-row">
        <Col>
          <div className="varnakrama-options">
            <div className="varnakrama-selects-group">
              <label className="varnakrama-label">
                <span className="label-text">वेदशाखा:</span>
                <select
                  className="varnakrama-select"
                  value={veda}
                  onChange={(e) => setVeda(e.target.value)}
                >
                  <option value="yajur">तैत्तिरीय</option>
                  <option value="rig">ऋग्वेद</option>
                  <option value="sama">सामवेद</option>
                  <option value="atharva">अथर्ववेद</option>
                </select>
              </label>

              <label className="varnakrama-label">
                <span className="label-text">वर्णक्रम-प्रकारः:</span>
                <select
                  className="varnakrama-select"
                  value={outputType}
                  onChange={(e) => setOutputType(e.target.value)}
                >
                  <option value="sarve">सर्वे (All)</option>
                  <option value="shuddha">शुद्ध</option>
                  <option value="svara">स्वर</option>
                  <option value="maatra">मात्रा</option>
                  <option value="anga">अङ्ग</option>
                  <option value="varnasaara">वर्णसार</option>
                </select>
              </label>
            </div>
          </div>
        </Col>
      </Row>

      {/* Input Row */}
      <Row className="varnakrama-input-row">
        <Col xs={12} className="varnakrama-col">
          <div className="varnakrama-section">
            <div className="varnakrama-section-header">
              <span className="section-label">निवेशः (Input)</span>
            </div>
            <textarea
              className="varnakrama-textarea varnakrama-input"
              value={inputWord}
              onChange={(e) => {
                setInputWord(e.target.value);
                setVarnasaaraData(null);
                setVarnasaaraError(null);
              }}
              placeholder="संस्कृत-पाठं लिखतु...&#10;(Enter Sanskrit text here)"
            />
          </div>
        </Col>
      </Row>

      {/* Actions Row */}
      <Row className="varnakrama-actions-row">
        <Col>
          <div className="varnakrama-actions">
            <Button
              className="varnakrama-submit-btn"
              onClick={handleAnalyze}
              disabled={loading || !inputWord.trim()}
            >
              {loading ? 'विश्लेषणम्...' : 'प्रदर्शय (Show)'}
            </Button>
            <Button
              variant="outline-secondary"
              className="varnakrama-clear-btn"
              onClick={handleClear}
              disabled={!inputWord && !result}
            >
              स्वच्छं कुरु (Clear)
            </Button>
          </div>
        </Col>
      </Row>

      {/* Results Tabs */}
      {(showAll || varnasaaraData) && (
        <Row className="varnakrama-tabs-row">
          <Col>
            <Tabs
              activeKey={activeTab}
              onSelect={handleTabSelect}
              className="mb-3 varnakrama-tabs"
            >
              <Tab eventKey="traditional" title="पारम्परिक (Traditional)">
                {showAll ? renderAllResults() : (
                  result && (
                    <div className="varnakrama-results">
                      <div className="varnakrama-result-content">
                        {result[outputType]}
                      </div>
                    </div>
                  )
                )}
              </Tab>

              <Tab eventKey="varnasaara" title="वर्णसार (Visual)">
                <VarnasaaraVisualization
                  data={varnasaaraData}
                  loading={varnasaaraLoading}
                  error={varnasaaraError}
                />
              </Tab>
            </Tabs>
          </Col>
        </Row>
      )}

      {/* Sanskrit Keyboard */}
      <Row className="varnakrama-keyboard-row">
        <Col>
          <SanskritKeyboard
            onKeyPress={handleKeyboardInput}
            isVisible={showKeyboard}
            onToggle={() => setShowKeyboard(!showKeyboard)}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default Varnakrama;
